<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Patient Insurance</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript: void(0);" @click="goTo('patient-list')">Patients</a></li>
                      <li class="breadcrumb-item active">Insurance</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Patient Summary Card -->
            <div v-if="patient" class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-body py-3">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center">
                        <div class="avatar-sm me-3">
                            <span class="avatar-title rounded-circle" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'">
                              {{ getInitials(patient.fullNames) }}
                            </span>
                        </div>
                        <div>
                          <h5 class="mb-0">{{ patient.fullNames }}</h5>
                          <small class="text-muted">{{ patient.patientID }} | {{ patient.gender }}, {{ patient.age }} yrs | {{ patient.phoneNumber }}</small>
                        </div>
                      </div>
                      <button class="btn btn-primary" @click="showAddModal = true">
                        <i class="mdi mdi-plus me-1"></i> Add Insurance
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted">Loading insurance information...</p>
            </div>

            <!-- No Insurance -->
            <div v-else-if="insurances.length === 0" class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body text-center py-5">
                    <div class="avatar-lg mx-auto mb-4">
                        <span class="avatar-title bg-light text-muted rounded-circle">
                          <i class="mdi mdi-shield-off font-size-48"></i>
                        </span>
                    </div>
                    <h5 class="text-muted">No Insurance Records</h5>
                    <p class="text-muted">This patient has no insurance cards registered</p>
                    <button class="btn btn-primary" @click="showAddModal = true">
                      <i class="mdi mdi-plus me-1"></i> Add Insurance Card
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Insurance Cards -->
            <div v-else class="row">
              <div v-for="ins in insurances" :key="ins.insuranceID" class="col-md-6 mb-4">
                <div class="card h-100" :class="{ 'border-primary': ins.isPrimary === 'Yes', 'border-danger': ins.status !== 'Active' || isExpired(ins.validTo) }">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <div>
                      <h5 class="mb-0">{{ ins.insuranceProvider }}</h5>
                      <small class="text-muted">{{ ins.insuranceType }}</small>
                    </div>
                    <div class="d-flex gap-2">
                      <span v-if="ins.isPrimary === 'Yes'" class="badge bg-primary">Primary</span>
                      <span class="badge" :class="{'bg-success': ins.status === 'Active' && !isExpired(ins.validTo), 'bg-danger': ins.status !== 'Active' || isExpired(ins.validTo)}">
                          {{ isExpired(ins.validTo) ? 'Expired' : ins.status }}
                        </span>
                    </div>
                  </div>

                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-6">
                        <label class="small text-muted">Member Number</label>
                        <p class="mb-0 fw-bold">{{ ins.memberNumber }}</p>
                      </div>
                      <div class="col-6">
                        <label class="small text-muted">Member Name</label>
                        <p class="mb-0">{{ ins.memberName }}</p>
                      </div>
                      <div class="col-6">
                        <label class="small text-muted">Scheme</label>
                        <p class="mb-0">{{ ins.scheme || '-' }}</p>
                      </div>
                      <div class="col-6">
                        <label class="small text-muted">Category</label>
                        <p class="mb-0">{{ ins.category || 'Principal' }}</p>
                      </div>
                      <div v-if="ins.category !== 'Principal'" class="col-12">
                        <label class="small text-muted">Principal Member</label>
                        <p class="mb-0">{{ ins.principalName }} ({{ ins.principalMemberNo }})</p>
                      </div>
                      <div class="col-6">
                        <label class="small text-muted">Employer</label>
                        <p class="mb-0">{{ ins.employer || '-' }}</p>
                      </div>
                      <div class="col-6">
                        <label class="small text-muted">Policy Number</label>
                        <p class="mb-0">{{ ins.policyNumber || '-' }}</p>
                      </div>
                    </div>

                    <hr class="my-3">

                    <!-- Cover Details -->
                    <div class="row g-3">
                      <div class="col-6">
                        <label class="small text-muted">Valid From</label>
                        <p class="mb-0">{{ formatDate(ins.validFrom) }}</p>
                      </div>
                      <div class="col-6">
                        <label class="small text-muted">Valid To</label>
                        <p class="mb-0" :class="{ 'text-danger': isExpired(ins.validTo) }">
                          {{ formatDate(ins.validTo) }}
                          <i v-if="isExpired(ins.validTo)" class="mdi mdi-alert-circle ms-1"></i>
                        </p>
                      </div>
                      <div class="col-12">
                        <label class="small text-muted">Cover Balance</label>
                        <div class="progress" style="height: 20px;">
                          <div class="progress-bar" :class="getCoverClass(ins)" :style="{ width: getCoverPercent(ins) + '%' }">
                            KES {{ numberFormat(ins.balanceAmount) }} / {{ numberFormat(ins.coverLimit) }}
                          </div>
                        </div>
                        <small class="text-muted">Used: KES {{ numberFormat(ins.usedAmount) }} ({{ getUsedPercent(ins) }}%)</small>
                      </div>
                    </div>

                    <hr class="my-3">

                    <!-- Verification Status -->
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                          <span class="badge" :class="{'bg-success': ins.verificationStatus === 'Verified', 'bg-warning': ins.verificationStatus === 'Pending', 'bg-danger': ins.verificationStatus === 'Failed'}">
                            <i class="mdi" :class="{'mdi-check-circle': ins.verificationStatus === 'Verified', 'mdi-clock': ins.verificationStatus === 'Pending', 'mdi-close-circle': ins.verificationStatus === 'Failed'}"></i>
                            {{ ins.verificationStatus }}
                          </span>
                        <small v-if="ins.lastVerifiedDate" class="text-muted ms-2">Last: {{ formatDate(ins.lastVerifiedDate) }}</small>
                      </div>
                      <button class="btn btn-sm btn-soft-primary" @click="verifyInsurance(ins)" :disabled="verifying === ins.insuranceID">
                        <span v-if="verifying === ins.insuranceID" class="spinner-border spinner-border-sm me-1"></span>
                        <i v-else class="mdi mdi-refresh me-1"></i> Verify
                      </button>
                    </div>
                  </div>

                  <div class="card-footer bg-white">
                    <div class="d-flex justify-content-between">
                      <div>
                        <button v-if="ins.isPrimary !== 'Yes'" class="btn btn-sm btn-soft-primary me-2" @click="setPrimary(ins)">Set as Primary</button>
                      </div>
                      <div>
                        <button class="btn btn-sm btn-soft-secondary me-2" @click="editInsurance(ins)"><i class="mdi mdi-pencil"></i> Edit</button>
                        <button class="btn btn-sm btn-soft-danger" @click="confirmDelete(ins)"><i class="mdi mdi-trash-can"></i></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add/Edit Insurance Modal -->
            <div class="modal fade" id="insuranceModal" tabindex="-1" ref="insuranceModal">
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title"><i class="mdi mdi-shield-plus me-2"></i>{{ editingInsurance ? 'Edit Insurance' : 'Add Insurance Card' }}</h5>
                    <button type="button" class="btn-close" @click="closeModal"></button>
                  </div>
                  <form @submit.prevent="saveInsurance">
                    <div class="modal-body">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label">Insurance Provider <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="form.insuranceProvider" required>
                            <option value="">Select Provider</option>
                            <option v-for="p in providers" :key="p" :value="p">{{ p }}</option>
                          </select>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Insurance Type</label>
                          <select class="form-select" v-model="form.insuranceType">
                            <option value="">Select Type</option>
                            <option v-for="t in ['National','Private','Corporate','Individual']" :key="t" :value="t">{{ t }}</option>
                          </select>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Member Number <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model="form.memberNumber" placeholder="Enter member number" required>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Member Name on Card</label>
                          <input type="text" class="form-control" v-model="form.memberName" placeholder="Name as it appears on card">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Scheme</label>
                          <input type="text" class="form-control" v-model="form.scheme" placeholder="e.g., Civil Servants">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Category</label>
                          <select class="form-select" v-model="form.category">
                            <option v-for="c in ['Principal','Spouse','Child','Dependent']" :key="c" :value="c">{{ c }}</option>
                          </select>
                        </div>
                        <template v-if="form.category !== 'Principal'">
                          <div class="col-md-6">
                            <label class="form-label">Principal Member Number</label>
                            <input type="text" class="form-control" v-model="form.principalMemberNo" placeholder="Principal's member number">
                          </div>
                          <div class="col-md-6">
                            <label class="form-label">Principal Name</label>
                            <input type="text" class="form-control" v-model="form.principalName" placeholder="Principal's name">
                          </div>
                        </template>
                        <div class="col-md-6">
                          <label class="form-label">Employer</label>
                          <input type="text" class="form-control" v-model="form.employer" placeholder="Employer name">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Policy Number</label>
                          <input type="text" class="form-control" v-model="form.policyNumber" placeholder="Policy/Group number">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Valid From</label>
                          <input type="date" class="form-control" v-model="form.validFrom">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Valid To</label>
                          <input type="date" class="form-control" v-model="form.validTo">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Cover Limit (KES)</label>
                          <input type="number" class="form-control" v-model="form.coverLimit" placeholder="Annual cover limit">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Card Serial Number</label>
                          <input type="text" class="form-control" v-model="form.cardSerialNo" placeholder="Card serial number">
                        </div>
                        <div class="col-12">
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="form.isPrimary" id="isPrimary">
                            <label class="form-check-label" for="isPrimary">Set as primary insurance</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
                      <button type="submit" class="btn btn-primary" :disabled="saving">
                        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                        {{ editingInsurance ? 'Update' : 'Add' }} Insurance
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div class="modal fade" id="deleteModal" tabindex="-1" ref="deleteModal">
              <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title text-danger"><i class="mdi mdi-alert me-2"></i>Confirm Delete</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this insurance record?</p>
                    <p class="mb-0"><strong>{{ deletingInsurance?.insuranceProvider }}</strong> - {{ deletingInsurance?.memberNumber }}</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="deleteInsurance" :disabled="deleting">
                      <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6"><div class="text-sm-end d-sm-block">County Hospital Management System</div></div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { execute, hospital } from "@/api";

export default {
  name: "PatientInsurance",
  components: { NavigationBar },
  props: { id: { type: String, required: true } },

  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      verifying: null,
      patient: null,
      insurances: [],
      showAddModal: false,
      editingInsurance: null,
      deletingInsurance: null,
      insuranceModal: null,
      deleteModalInstance: null,
      providers: ['NHIF','SHA','AAR','Jubilee','Madison','UAP','Britam','CIC','Resolution','APA','Other'],
      form: {
        insuranceProvider: '',
        insuranceType: '',
        memberNumber: '',
        memberName: '',
        scheme: '',
        category: 'Principal',
        principalMemberNo: '',
        principalName: '',
        employer: '',
        policyNumber: '',
        coverLimit: '',
        validFrom: '',
        validTo: '',
        cardSerialNo: '',
        isPrimary: false
      }
    };
  },

  watch: {
    showAddModal(val) { if (val) this.insuranceModal.show(); }
  },

  mounted() {
    this.insuranceModal = new window.bootstrap.Modal(this.$refs.insuranceModal);
    this.deleteModalInstance = new window.bootstrap.Modal(this.$refs.deleteModal);
    this.$refs.insuranceModal.addEventListener('hidden.bs.modal', () => { this.showAddModal = false; this.editingInsurance = null; this.resetForm(); });
    this.loadPatient();
    this.loadInsurances();
  },

  methods: {
    goTo(route) { this.$router.push(route); },
    numberFormat(num) { return new Intl.NumberFormat("en-GB").format(num || 0); },

    loadPatient() {
      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("patientID", this.id);
      data.append("singleResult", "true");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        if (res.data.success) this.patient = res.data.data.patient;
      }).catch(e => console.error('Error:', e));
    },

    loadInsurances() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getPatientInsurance");
      data.append("patientID", this.id);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        this.loading = false;
        if (res.data.success) this.insurances = res.data.data.insurances || [];
      }).catch((e) => { this.loading = false; console.error('Error:', e); });
    },

    resetForm() {
      this.form = { insuranceProvider: '', insuranceType: '', memberNumber: '', memberName: '', scheme: '', category: 'Principal', principalMemberNo: '', principalName: '', employer: '', policyNumber: '', coverLimit: '', validFrom: '', validTo: '', cardSerialNo: '', isPrimary: false };
    },

    editInsurance(ins) {
      this.editingInsurance = ins;
      this.form = { ...ins, isPrimary: ins.isPrimary === 'Yes' };
      this.insuranceModal.show();
    },

    saveInsurance() {
      this.saving = true;
      const data = new FormData();
      data.append("function", this.editingInsurance ? "updatePatientInsurance" : "addPatientInsurance");
      data.append("patientID", this.id);
      data.append("patientName", this.patient?.fullNames);
      Object.keys(this.form).forEach(key => data.append(key, key === 'isPrimary' ? (this.form[key] ? 'Yes' : 'No') : (this.form[key] || '')));
      if (this.editingInsurance) data.append("insuranceID", this.editingInsurance.insuranceID);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital).then((res) => {
        this.saving = false;
        if (res.data.success) { this.closeModal(); this.loadInsurances(); }
        else alert(res.data.message || 'Failed to save');
      }).catch((e) => { this.saving = false; alert('An error occurred'); console.error('Error:', e); });
    },

    verifyInsurance(ins) {
      this.verifying = ins.insuranceID;
      const data = new FormData();
      data.append("function", "verifyInsurance");
      data.append("insuranceID", ins.insuranceID);
      data.append("memberNumber", ins.memberNumber);
      data.append("provider", ins.insuranceProvider);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        this.verifying = null;
        if (res.data.success) this.loadInsurances();
        else alert(res.data.message || 'Verification failed');
      }).catch((e) => { this.verifying = null; alert('Verification failed'); console.error('Error:', e); });
    },

    setPrimary(ins) {
      const data = new FormData();
      data.append("function", "updatePatientInsurance");
      data.append("insuranceID", ins.insuranceID);
      data.append("isPrimary", "Yes");
      data.append("patientID", this.id);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => { if (res.data.success) this.loadInsurances(); }).catch(e => console.error('Error:', e));
    },

    confirmDelete(ins) { this.deletingInsurance = ins; this.deleteModalInstance.show(); },

    deleteInsurance() {
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deletePatientInsurance");
      data.append("insuranceID", this.deletingInsurance.insuranceID);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        this.deleting = false;
        if (res.data.success) { this.deleteModalInstance.hide(); this.loadInsurances(); }
        else alert(res.data.message || 'Failed to delete');
      }).catch((e) => { this.deleting = false; alert('Delete failed'); console.error('Error:', e); });
    },

    closeModal() { this.insuranceModal.hide(); },
    isExpired(date) { return date ? new Date(date) < new Date() : false; },
    getCoverPercent(ins) { if (!ins.coverLimit || ins.coverLimit === '0') return 100; return Math.min(100, ((parseFloat(ins.balanceAmount) || 0) / (parseFloat(ins.coverLimit) || 1)) * 100); },
    getUsedPercent(ins) { if (!ins.coverLimit || ins.coverLimit === '0') return 0; return Math.round(((parseFloat(ins.usedAmount) || 0) / (parseFloat(ins.coverLimit) || 1)) * 100); },
    getCoverClass(ins) { const p = this.getCoverPercent(ins); return p > 50 ? 'bg-success' : p > 20 ? 'bg-warning' : 'bg-danger'; },
    formatDate(date) { if (!date) return '-'; return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); },
    getInitials(name) { if (!name) return '?'; return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase(); }
  }
}
</script>

<style scoped>
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #fff; font-weight: 600; }
.bg-pink { background-color: #e83e8c !important; }
.btn-soft-primary { background-color: rgba(85, 110, 230, 0.1); color: #556ee6; border: none; }
.btn-soft-primary:hover { background-color: #556ee6; color: #fff; }
.btn-soft-secondary { background-color: rgba(116, 120, 141, 0.1); color: #74788d; border: none; }
.btn-soft-secondary:hover { background-color: #74788d; color: #fff; }
.btn-soft-danger { background-color: rgba(244, 106, 106, 0.1); color: #f46a6a; border: none; }
.btn-soft-danger:hover { background-color: #f46a6a; color: #fff; }
</style>